// import external libraries
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import components
import { ControlledAutoComplete } from 'components/shared/ControlledFields';
import {
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import BacklogActivityFilter from '../BacklogActivityFilter';
import BacklogEmployeeViewFields from '../BacklogEmployeeViewFields';
import BacklogEntitySearch from '../BacklogEntitySearch';
import BacklogFilterSectionHeading from '../BacklogFilterSectionHeading';

// import styles
import {
  BacklogFilterFieldBox,
  BacklogFilterSectionStack
} from '../../../../Backlog.styles';

// import types
import type {
  BacklogEntityFilterType,
  BacklogFilterFieldsProps,
  BacklogFiltersForm
} from '../../BacklogFilters.types';

/**
 * The body of the filter popover: the activity, then the filters of the entity whose list is on screen.
 *
 * Only that entity's filters are shown, under a heading carrying that entity's symbol and name so it is clear
 * they narrow that list alone. The activity is the exception — it means the same thing whichever list you are
 * on, so it sits above the heading. A filter picked for another entity type is remembered and applies
 * again when you switch to that view, but it does not narrow the list you are looking at now: what you see
 * is what filters.
 *
 * Fields for filters that have no backend yet are shown disabled, the treatment the tiles give a category
 * still to come — the planner sees what is coming without being able to ask for it.
 */
const BacklogFilterFields = ({
  activityOptions,
  rowEntityType,
  startDate,
  endDate
}: BacklogFilterFieldsProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'backlog' });
  // The entity type names sit under the mrp namespace, shared with afsprakenplanning's own pickers, so the
  // fields are named the same there as here. The count wording ("2 geselecteerd") sits at the root of the
  // namespace too, shared with the app's other compact multi-selects.
  const { t: tCommon } = useTranslation('common');
  const { control } = useFormContext<BacklogFiltersForm>();
  // The employee and function lists are the plan unit's own, so they follow the picks above them as they are
  // made rather than waiting for Toepassen. useWatch rather than watch(): it subscribes properly, where a
  // watch() call in a 'use memo' component can be memoised into a stale value.
  const entitySearch = (entityType: BacklogEntityFilterType) => (
    <BacklogFilterFieldBox>
      <Controller
        name={entityType}
        control={control}
        render={({ field }) => (
          <BacklogEntitySearch
            entityType={entityType}
            label={tCommon(`mrp.entityTypeMap.${entityType}.single`)}
            selected={field.value}
            onChange={field.onChange}
            // Exactly one care receiver is the smallest question the takenlijst can answer in the patient
            // view, so the field says so rather than letting the planner discover an empty list.
            required={entityType === 'CLIENT'}
          />
        )}
      />
    </BacklogFilterFieldBox>
  );

  /** A filter that has no backend yet: shown so it is clearly coming, disabled so it cannot be asked for. */
  const placeholderField = (name: string, label: string) => (
    <BacklogFilterFieldBox>
      <ControlledAutoComplete
        disabled
        disableSorting
        control={control}
        name={name}
        label={label}
        options={[]}
        variant="outlined"
        fullWidth
      />
    </BacklogFilterFieldBox>
  );

  const fieldsForView = () => {
    switch (rowEntityType) {
      case 'EMPLOYEE':
        return (
          <BacklogEmployeeViewFields startDate={startDate} endDate={endDate} />
        );
      case 'WORKPLACE':
        return <GridCompact6>{entitySearch('WORKPLACE')}</GridCompact6>;
      case 'RESOURCE':
        return <GridCompact6>{entitySearch('RESOURCE')}</GridCompact6>;
      default:
        return (
          <>
            <GridCompact6>
              {placeholderField('treatmentGroup', t('filters.treatmentGroup'))}
            </GridCompact6>
            <GridCompact6>{entitySearch('CLIENT')}</GridCompact6>
          </>
        );
    }
  };

  return (
    <BacklogFilterSectionStack>
      <GridContainer spacing={3} width={1}>
        <GridCompact6>
          <BacklogFilterFieldBox>
            <Controller
              name="activity"
              control={control}
              render={({ field }) => (
                <BacklogActivityFilter
                  options={activityOptions}
                  selected={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </BacklogFilterFieldBox>
        </GridCompact6>
      </GridContainer>

      <BacklogFilterSectionHeading entityType={rowEntityType} />

      <GridContainer spacing={3} width={1}>
        {fieldsForView()}
      </GridContainer>
    </BacklogFilterSectionStack>
  );
};

export default BacklogFilterFields;
